import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useComensalesModal } from '../../services/hooks/useComensalesModal';
import { useTurnos } from '../../services/hooks/useTurnos';
import { useComensales } from '../../services/hooks/useComensales';
import { useCargaDatosTurnoDetalleInMemory } from '../../services/hooks/useCargaDatosTurnoDetalleInMemory';
import { getPeruDateTimeSqlString } from '../../utils/dateUtils';

const ModalTurnoDetalle = ({
  turnoSeleccionado: turnoSeleccionadoProp, // Contiene comedor y tipo servicio completos
  isOpen: isOpenProp,
  onClose: onCloseProp,
  showToast: showToastProp,
  // Cliente se obtiene del comensal - no necesitamos prop
}) => {
  // ✅ OPTIMIZADO: Proyecto viene completo en el turno - no necesitamos localStorage
  const selectedProject = useMemo(() => 
    turnoSeleccionadoProp?.proyecto || { id: turnoSeleccionadoProp?.id_proyecto },
    [turnoSeleccionadoProp?.proyecto, turnoSeleccionadoProp?.id_proyecto]
  );
  
  // Hooks para API calls - Solo las funcion que necesitamos
  const { validarAccesoComensalApi } = useComensalesModal();
  const { getDocumentosRegistradosByTurnoApi, createTurnoDetalle } = useTurnos();
  const { fetchDietasActivas } = useComensales();
  
  // Estados del componente - DEBEN ir ANTES del hook personalizado
  const [numeroDocumentoComedor, setNumeroDocumentoComedor] = useState("");
  const [cantidadComensales, setCantidadComensales] = useState(0);
  
  // Hook personalizado para carga de datos en memoria - DESPUÉS de los estados
  const {
    isLoading: isLoadingMemoryData,
    memoryDataLoaded,
    documentosComedorInMemory,
    setDocumentosComedorInMemory,
    dietasComensalesInMemory,
    loadDatosInMemory
  } = useCargaDatosTurnoDetalleInMemory(
    isOpenProp,
    turnoSeleccionadoProp,
    selectedProject,
    getDocumentosRegistradosByTurnoApi,
    fetchDietasActivas,
    setCantidadComensales
  );
  const [resultadoRegistroComensal, setResultadoRegistroComensal] = useState(null);
  const [recentRecords, setRecentRecords] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dietaActivada, setDietaActivada] = useState(null);
      
  // Estados para manejar múltiples comensales activos
  const [showComensalSelector, setShowComensalSelector] = useState(false);
  const [comensalesMultiples, setComensalesMultiples] = useState([]);
  const [documentoAmbiguo, setDocumentoAmbiguo] = useState("");

  const numeroDocumentoComedorInputRef = useRef(null);

  // ✅ FUNCIÓN UTILIDAD: Auto-enfoque en el input del documento
  const enfocarInputDocumento = useCallback((delay = 100, contexto = 'general') => {
    setTimeout(() => {
      if (numeroDocumentoComedorInputRef.current) {
        const input = numeroDocumentoComedorInputRef.current;
        
        // Verificar si el input está habilitado
        if (input.disabled) {
          setTimeout(() => enfocarInputDocumento(100, `${contexto} (reintento)`), 200);
          return;
        }
        
        input.focus();
        input.select(); // Seleccionar todo el texto si hay alguno
        console.log(`🎯 Foco automático establecido en input del documento - Contexto: ${contexto}`);
      }
    }, delay);
  }, []);
    
// Cargar datos iniciales cuando se ABRE el modal
useEffect(() => {
  if (isOpenProp && turnoSeleccionadoProp) {
    console.log('Modal abierto - Inicializando...');
    console.log('Limpiando memoria anterior y cargando documentos del turno:', turnoSeleccionadoProp.id);
    loadDatosInMemory(); // ✅ Usar el hook personalizado
  }
}, [isOpenProp, turnoSeleccionadoProp, loadDatosInMemory]);

  // Detectar cuando todos los datos estén cargados en memoria
  useEffect(() => {
    console.log('🔍 DEBUG - Estado de carga de memoria:', {
      isLoadingMemoryData,
      memoryDataLoaded,
      documentos: memoryDataLoaded.loadStateDocumentosComedor,
      dietas: memoryDataLoaded.loadStateDietasComensal
    });
  
    // ✅ Cuando ambos estados estén cargados y el modal esté listo
    if (memoryDataLoaded.loadStateDocumentosComedor && memoryDataLoaded.loadStateDietasComensal && !isLoadingMemoryData) {
      console.log('🎉 TODOS LOS DATOS CARGADOS EN MEMORIA - Modal listo para uso');
      
      // ✅ RESTAURAR: Auto-enfoque en el textbox después de cargar datos en memoria
      enfocarInputDocumento(150, 'carga en memoria completada');
    }
  }, [memoryDataLoaded, isLoadingMemoryData, enfocarInputDocumento]);
  
  // Constante para tipo de entrada automático (ID = 1, código 'A')
  const TIPO_ENTRADA_AUTOMATICO_ID = 1;

  // El comedor ya viene completo en el turno, no necesitamos buscarlo
  // const comedorActual = turnoSeleccionadoProp?.comedor; // No usado actualmente

  // El tipo de servicio también viene completo en el turno
  const tipoServicioAlimentacionActual = turnoSeleccionadoProp?.tipo_servicio_alimentacion;

  // Determinar si el modal está listo para usar
  const isModalReady = !isLoadingMemoryData && memoryDataLoaded.loadStateDocumentosComedor && memoryDataLoaded.loadStateDietasComensal;
  
  // Log adicional para debugging del estado final
  useEffect(() => {
    console.log('🎯 ESTADO FINAL - isModalReady:', isModalReady, {
      isLoadingMemoryData,
      documentos: memoryDataLoaded.loadStateDocumentosComedor,
      dietas: memoryDataLoaded.loadStateDietasComensal
    });
  }, [isModalReady, isLoadingMemoryData, memoryDataLoaded]);

  const validarAccesoComensal = useCallback(async (startTime) => {
    const step1Time = performance.now();
  
    // Obtener id del tipo de servicio de alimentación del turno
    // Ya validado en GestionarTurnos antes de abrir el modal, por lo que siempre estará presente
    const tipoServicioAlimentacionId = tipoServicioAlimentacionActual?.id;
  
    const result = await validarAccesoComensalApi(
      numeroDocumentoComedor,
      selectedProject.id,
      turnoSeleccionadoProp.id_comedor,
      tipoServicioAlimentacionId,
      turnoSeleccionadoProp?.fecha_turno
    );
  
    console.log('✅ PASO 1 Validando acceso del comensal completado en:', (performance.now() - step1Time).toFixed(2) + 'ms');
  
    // Validar que result exista
    if (!result) {
      console.error('❌ Resultado inválido de validarAccesoComensalApi:', result);
      setResultadoRegistroComensal({
        error: "Error: No se recibió respuesta del servidor",
        color: "red"
      });
      return null;
    }
  
    // Verificar si hay múltiples comensales activos
    if (result.error_type === 'MULTIPLE_ACTIVE_COMENSALES') {
      console.log('🚨 MÚLTIPLES COMENSALES DETECTADOS:', result.comensales);
      
      // Validar que comensales sea un array válido
      const comensalesArray = Array.isArray(result.comensales) ? result.comensales : [];
      
      // Enriquecer comensales con sus dietas desde memoria
      const comensalesConDietas = comensalesArray.map(comensal => {
        const dietaEnMemoria = dietasComensalesInMemory.get(numeroDocumentoComedor);
        return {
          ...comensal,
          dieta_activa: dietaEnMemoria || null
        };
      });
      
      setComensalesMultiples(comensalesConDietas);
      setDocumentoAmbiguo(numeroDocumentoComedor);
      setShowComensalSelector(true);
      setResultadoRegistroComensal({
        error: result.message || "Documento ambiguo: existe en múltiples empresas activas",
        color: result.color || "orange"
      });
      return null; // Indicar que no se debe continuar
    }
  
    // Si tiene acceso, retornar el comensal
    if (result.hasAccess === true || result.success === true) {
      // Validar que los campos requeridos estén presentes
      if (!result.id || !result.numero_documento_comedor) {
        console.error('❌ Comensal con acceso pero datos incompletos:', result);
        setResultadoRegistroComensal({
          error: "Error: Datos incompletos del comensal",
          color: "red"
        });
        return null;
      }
      
      console.log('🔍 DEBUG - Datos del comensal recibidos:', result);
      
      // Buscar dieta en memoria usando numero_documento_comedor
      const dietaEnMemoria = dietasComensalesInMemory.get(result.numero_documento_comedor);
      console.log('🔍 DEBUG - Dieta desde memoria:', dietaEnMemoria);
      
      // Agregar dieta al resultado
      const comensalConDieta = {
        ...result,
        dieta_activa: dietaEnMemoria || null
      };
      
      console.log('🔍 DEBUG - Comensal final con dieta:', comensalConDieta);
      return comensalConDieta; // Devolver el comensal con dieta
    } else {
      // Acceso denegado o error
      console.log('❌ Acceso denegado - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
      const errorMessage = result?.message || "Acceso denegado";
      setResultadoRegistroComensal({
        error: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        color: result?.color || "red"
      });
      return null; // Indicar que no se debe continuar
    }
  }, [validarAccesoComensalApi, numeroDocumentoComedor, selectedProject.id, turnoSeleccionadoProp.id_comedor, turnoSeleccionadoProp?.fecha_turno, tipoServicioAlimentacionActual, dietasComensalesInMemory]);
  // }, [validarAccesoComensalApi, numeroDocumentoComedor, selectedProject.id, turnoSeleccionadoProp.id_comedor, dietasComensalesInMemory]); // COMENTADO: Para PHP (rollback disponible)


  /**
   * Función para validar si el documento ya está registrado en el turno (usando memoria)
   * SÚPER RÁPIDA - Validación O(1) usando Set en memoria
   * @param {string} numeroDocumento - Número de documento del comensal a validar
   * @param {number} startTime - Timestamp del inicio del proceso completo (para logging)
   * @returns {boolean} - true si el documento está disponible (no registrado), false si ya está registrado
   */
  const validateDocumentoEnMemoriaByTurnoDetalle = useCallback((numeroDocumento, startTime) => {
    const step2Time = performance.now();
    
    // Validación O(1) usando Set
    const isDocumentRegistered = documentosComedorInMemory.has(numeroDocumento);
    
    const validationTime = (performance.now() - step2Time).toFixed(2);
    console.log(`🧠 PASO 2 - Validación en MEMORIA completada en: ${validationTime}ms`);
    console.log('🔍 DEBUGGING - Validación en memoria:', {
      numeroDocumento,
      isRegistered: isDocumentRegistered,
      totalDocumentosEnMemoria: documentosComedorInMemory.size
    });

    // Si el documento ya está registrado en el turno
    if (isDocumentRegistered) {
      console.log(`❌ Documento ya registrado - Tiempo total: ${(performance.now() - startTime).toFixed(2)}ms`);
      setResultadoRegistroComensal({ 
        error: "Documento ya registrado en turno", 
        color: "red"
      });
      return false;
    }

    console.log(`✅ Documento disponible - Validación en memoria: ${validationTime}ms`);
    return true;
  }, [documentosComedorInMemory]);

  const prepararDatosTurnoDetalle = useCallback((comensal) => {
    const fechaRegistroPeru = getPeruDateTimeSqlString();

    return {
      id_turno_cabecera: turnoSeleccionadoProp.id,
      id_comensal: comensal.id,
      id_tipo_entrada: TIPO_ENTRADA_AUTOMATICO_ID,
      id_cliente: comensal.id_cliente,
      id_proyecto: selectedProject.id,
      id_comedor: turnoSeleccionadoProp.id_comedor,
      precio_venta: tipoServicioAlimentacionActual?.valor_servicio || 0,
      fecha_registro: fechaRegistroPeru
    };
  }, [turnoSeleccionadoProp.id, selectedProject.id, turnoSeleccionadoProp.id_comedor, tipoServicioAlimentacionActual, TIPO_ENTRADA_AUTOMATICO_ID]);

  const updateUI = useCallback((comensal, startTime) => {
    const step5Time = performance.now();
  
    // Construir mensaje con información de dieta
    let mensaje = "Acceso Autorizado";
    console.log('🔍 DEBUG - Construyendo mensaje con comensal:', comensal);
    console.log('🔍 DEBUG - comensal.dieta_activa:', comensal.dieta_activa);
    
    if (comensal.dieta_activa) {
      console.log('🔍 DEBUG - Tipo dieta:', comensal.dieta_activa.tipo_dieta);
      mensaje += ` - Dieta: ${comensal.dieta_activa.tipo_dieta.nombre}`;
      setDietaActivada(comensal.dieta_activa); // Guardar dieta en estado
    } else {
      setDietaActivada(null); // Limpiar si no hay dieta
    }
    console.log('🔍 DEBUG - Mensaje final:', mensaje);
  
    setResultadoRegistroComensal({ ...comensal, message: mensaje, color: "green", hasAccess: true });
    setCantidadComensales(prevCount => prevCount + 1);
      
    // Agregar el registro a la lista de registros recientes
    const nombreConTipoOrigen = comensal.tipo_origen_trabajador?.nombre
      ? `${comensal.nombre} - ${comensal.tipo_origen_trabajador.nombre}`
      : comensal.nombre;
    
    const newRecord = {
      id: Date.now(), // ID único temporal para nuevos registros
      nombre: nombreConTipoOrigen,
      numero_documento_comedor: numeroDocumentoComedor,
      cliente: comensal.cliente?.nombre || 'Cliente no disponible',
      timestamp: new Date().toLocaleTimeString('es-PE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      isNewRecord: true // Marcar como nuevo registro para destacarlo
    };
  
    // Agregar al principio de la lista y mantener solo los últimos 5
    setRecentRecords(prevRecords => {
      const updatedRecords = [newRecord, ...prevRecords];
      return updatedRecords.slice(0, 5);
    });
  
    console.log('✅ PASO 5 completado en:', (performance.now() - step5Time).toFixed(2) + 'ms');
    console.log('🏁 PROCESO COMPLETO - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
  }, [numeroDocumentoComedor]);

  const handleKeyDown = useCallback(
    async (event) => {
      if (event.key === "Enter") {
        if (isProcessing) return; // Evita doble submit
        
        // Verificar que el modal esté listo
        if (!isModalReady) {
          setResultadoRegistroComensal({ 
            error: "Cargando datos en memoria, espere un momento...", 
            color: "orange" 
          });
          return;
        }
        
        if (!numeroDocumentoComedor.trim()) {
          setResultadoRegistroComensal({ error: "Ingrese un número de documento", color: "red" });
          return;
        }

        setIsProcessing(true);
        setResultadoRegistroComensal(null);
        
        const startTime = performance.now();
        console.log('🚀 INICIO - Registro de comensal:', numeroDocumentoComedor);
        
        try {
         
          const comensal = await validarAccesoComensal(startTime);
          if (!comensal) return; // Si no tiene acceso, salir del proceso
            
          const turnoValidationPassed = validateDocumentoEnMemoriaByTurnoDetalle(numeroDocumentoComedor, startTime);
            
          if (!turnoValidationPassed) return; // Si la validación falló, salir del proceso
                                                                                          
          const step3Time = performance.now();            
            
          const turnoDetalleData = prepararDatosTurnoDetalle(comensal);
            
          console.log('📦 Datos preparados:', turnoDetalleData);
          console.log('✅ PASO 3 Preparando datos para INSERT completado en:', (performance.now() - step3Time).toFixed(2) + 'ms');
            
          try {
            // 🚀 PASO 4: Enviar al backend
            const step4Time = performance.now();              
              
            const response = await createTurnoDetalle(turnoDetalleData);
              
            console.log('✅ PASO 4 createTurnoDetalle en el backend , completado en:', (performance.now() - step4Time).toFixed(2) + 'ms');
            console.log('📦 Respuesta del backend:', response);
              
            // Verificar si la respuesta indica éxito
            // El backend devuelve directamente el objeto TurnoDetalleResponse con id
            if (response && (response.id || response.data?.id)) {
              
              // 🧠 ACTUALIZAR MEMORIA: Agregar documento a la memoria para futuras validaciones
              setDocumentosComedorInMemory(prevDocumentos => {
                const newSet = new Set(prevDocumentos);
                newSet.add(numeroDocumentoComedor);
                console.log(`🧠 Documento ${numeroDocumentoComedor} agregado a memoria. Total: ${newSet.size}`);
                return newSet;
              });
              
              updateUI(comensal, startTime);                            
                                          
            } else {
              console.log('❌ ERROR backend - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
              const errorMessage = response?.error || response?.message || "Error al registrar el turno";
              setResultadoRegistroComensal({ error: errorMessage, color: "red" });
            }
          } catch (err) {
            console.log('❌ ERROR excepción - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
            const errorMessage = err?.response?.data?.error || 
                               err?.response?.data?.message || 
                               "Error al registrar el turno";
            setResultadoRegistroComensal({ error: errorMessage, color: "red" });
          }
        } catch (error) {
          console.log('❌ ERROR general - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
          console.error('❌ ERROR completo:', error);
          
          // CORREGIDO: Manejo seguro de error.message
          let errorMessage = "Error al buscar comensal";
          
          if (error && typeof error === 'object') {
            if (typeof error.message === 'string') {
              errorMessage += ": " + error.message;
            } else if (error.response?.data?.message) {
              errorMessage += ": " + error.response.data.message;
            } else if (error.response?.data?.error) {
              errorMessage += ": " + error.response.data.error;
            } else {
              errorMessage += ": Error desconocido";
            }
          } else if (typeof error === 'string') {
            errorMessage += ": " + error;
          }
          
          setResultadoRegistroComensal({ error: errorMessage, color: "red" });
        } finally {
          setIsProcessing(false); // Siempre se ejecuta, incluso si hay error
          setNumeroDocumentoComedor(""); // Limpiar el campo
          
          // Poner el foco de nuevo en el input después de que React complete el re-render
          // Usar requestAnimationFrame para asegurar que el DOM esté actualizado
          requestAnimationFrame(() => {
            enfocarInputDocumento(50, 'después de procesamiento');
          });
        }
      }
    },
    [numeroDocumentoComedor, isProcessing, isModalReady, validateDocumentoEnMemoriaByTurnoDetalle, enfocarInputDocumento, validarAccesoComensal, prepararDatosTurnoDetalle, createTurnoDetalle, updateUI, setDocumentosComedorInMemory]   
  );

  // Función para manejar la selección de un comensal específico
  const handleComensalSelection = useCallback(async (comensalSeleccionado) => {
    console.log('🎯 Comensal seleccionado:', comensalSeleccionado);
    
    // Cerrar el modal de selección
    setShowComensalSelector(false);
    
    // Proceder con el registro usando el comensal seleccionado
    const startTime = performance.now();
    
    try {
      // 🔧 PASO 1: Validaciones previas
      const step1Time = performance.now();
      console.log('🔧 PASO 1: Validaciones previas...');
      
      const comensal = {
        id: comensalSeleccionado.id,
        id_cliente: comensalSeleccionado.id_cliente,
        nombre: comensalSeleccionado.nombre,
        numero_documento_comedor: documentoAmbiguo,
        cliente: comensalSeleccionado.cliente,
        hasAccess: true
      };
      
      console.log('🔍 DEBUGGING - Objeto comensal seleccionado:', comensal);
      
      if (!comensal.id_cliente) {
        console.log('❌ Error cliente - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
        setResultadoRegistroComensal({ error: "Error: Comensal sin cliente asignado", color: "red" });
        setNumeroDocumentoComedor("");
        return;
      }
      
      console.log('✅ PASO 1 completado en:', (performance.now() - step1Time).toFixed(2) + 'ms');
      
      // 📦 PASO 2: Preparar datos
      const step2Time = performance.now();
      console.log('📦 PASO 2: Preparando datos para INSERT...');
      
      const turnoData = {
        id_turno_cabecera: turnoSeleccionadoProp.id,
        id_comensal: comensal.id,
        id_tipo_entrada: TIPO_ENTRADA_AUTOMATICO_ID,
        id_cliente: comensal.id_cliente,
        id_proyecto: selectedProject.id,
        id_comedor: turnoSeleccionadoProp.id_comedor,
        precio_venta: tipoServicioAlimentacionActual?.valor_servicio || 0,
        fecha_registro: getPeruDateTimeSqlString(),
      };
      
      console.log('📦 Datos preparados:', turnoData);
      console.log('✅ PASO 2 completado en:', (performance.now() - step2Time).toFixed(2) + 'ms');
      
      // 🚀 PASO 3: Crear turno
      const step3Time = performance.now();
      console.log('🚀 PASO 3: Creando turno detalle...');
      
      const turnoResponse = await createTurnoDetalle(turnoData);
      
      console.log('✅ PASO 3 completado en:', (performance.now() - step3Time).toFixed(2) + 'ms');
      console.log('📦 Respuesta del backend:', turnoResponse);
      
      // El backend devuelve directamente el objeto TurnoDetalleResponse con id
      if (turnoResponse && (turnoResponse.id || turnoResponse.data?.id)) {
        // ✅ Registro exitoso
        console.log('✅ Registro exitoso - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
        
        const nombreConTipoOrigen = comensalSeleccionado.tipo_origen_trabajador?.nombre
          ? `${comensal.nombre} - ${comensalSeleccionado.tipo_origen_trabajador.nombre}`
          : comensal.nombre;
        
        setResultadoRegistroComensal({
          message: `${nombreConTipoOrigen} - ${comensal.cliente?.nombre || 'Cliente no disponible'}`,
          color: "green"
        });
        
        // Actualizar contador y registros
        setCantidadComensales(prev => prev + 1);
        
        // Agregar a registros recientes
        const nuevoRegistro = {
          id: Date.now(),
          nombre: nombreConTipoOrigen,
          numero_documento_comedor: numeroDocumentoComedor,
          cliente: comensal.cliente?.nombre || 'Cliente no disponible',
          timestamp: new Date().toLocaleTimeString('es-PE', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit' 
          }),
          isNewRecord: true
        };
        setRecentRecords(prev => [nuevoRegistro, ...prev.slice(0, 4)]);
        
        console.log('🏁 PROCESO COMPLETO - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
      } else {
        console.log('❌ ERROR backend - Tiempo total:', (performance.now() - startTime).toFixed(2) + 'ms');
        setResultadoRegistroComensal({ 
          error: "Error al registrar el turno", 
          color: "red" 
        });
      }
    } catch (error) {
      console.error('❌ Error en registro:', error);
      setResultadoRegistroComensal({ 
        error: "Error al registrar el turno", 
        color: "red" 
      });
    }
  }, [createTurnoDetalle, turnoSeleccionadoProp, selectedProject, documentoAmbiguo, TIPO_ENTRADA_AUTOMATICO_ID, numeroDocumentoComedor, tipoServicioAlimentacionActual]);

  // Función para limpiar el estado cuando se cierra el modal principal
  // const handleCloseMainModal = useCallback(() => {
  //   setShowComensalSelector(false);
  //   setComensalesMultiples([]);
  //   setDocumentoAmbiguo("");
  //   setNumeroDocumentoComedor("");
  //   setResultadoRegistroComensal(null);
  // }, []);

  if (!isOpenProp) return null;

  // 🚀 POPUP DE CARGA INICIAL - Mostrar mientras se cargan los datos en memoria
  if (isLoadingMemoryData) {
    return (
      <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '15px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-body text-center p-5" style={{ color: 'white' }}>
              <div className="mb-4">
                <div className="spinner-border text-light" role="status" style={{ 
                  width: '4rem', 
                  height: '4rem',
                  borderWidth: '4px'
                }}>
                  <span className="visually-hidden">Cargando...</span>
                </div>
              </div>
              
              <h4 className="mb-3" style={{ fontWeight: '600' }}>
                🚀 Inicializando Control de Acceso Comensales
              </h4>
              
              <p className="mb-4" style={{ fontSize: '1.0rem', opacity: '0.9' }}>
                Cargando datos en memoria para máxima velocidad...
              </p>
              
              <div className="row text-center">
                <div className="col-6">
                  <div className="mb-2">
                    {memoryDataLoaded.loadStateDocumentosComedor ? 
                      <i className="fas fa-check-circle text-success" style={{ fontSize: '1.4rem' }}></i> :
                      <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                    }
                  </div>
                  <small style={{ opacity: '0.8' }}>
                    Documentos<br/>Registrados
                  </small>
                </div>
                
                <div className="col-6">
                  <div className="mb-2">
                    {memoryDataLoaded.loadStateDietasComensal ? 
                      <i className="fas fa-check-circle text-success" style={{ fontSize: '1.4rem' }}></i> :
                      <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                    }
                  </div>
                  <small style={{ opacity: '0.8' }}>
                    Dietas<br/>Comensales
                  </small>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="progress" style={{ height: '8px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <div 
                    className="progress-bar bg-success" 
                    role="progressbar" 
                    style={{ 
                      width: `${((memoryDataLoaded.loadStateDocumentosComedor ? 50 : 0) + (memoryDataLoaded.loadStateDietasComensal ? 50 : 0))}%`,
                      borderRadius: '10px',
                      transition: 'width 0.5s ease'
                    }}
                  ></div>
                </div>
                <small className="mt-2 d-block" style={{ opacity: '0.7' }}>
                  {((memoryDataLoaded.loadStateDocumentosComedor ? 50 : 0) + (memoryDataLoaded.loadStateDietasComensal ? 50 : 0))}% completado
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Font Awesome para iconos */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" />
      
      {/* Digital Harmony Styles para Modal */}
      <style>
        {`
          /* Animaciones */
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .new-record-pulse {
            animation: fadeIn 0.5s ease-in;
          }
          
          /* Modal backdrop personalizado */
          .dh-modal-backdrop {
            background: linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%);
            backdrop-filter: blur(8px);
          }
          
          /* Loading styles eliminados - ya no hay carga asíncrona */
          
          /* Modal content con Digital Harmony */
          .dh-modal-content {
            border: none;
            border-radius: 0;
            box-shadow: 0 25px 50px -12px rgba(102, 126, 234, 0.15);
            background: linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%);
            overflow: hidden;
          }
          
          /* Header del modal */
          .dh-modal-header {
            background: linear-gradient(135deg, #2c5aa0 0%, #1e3d72 100%);
            color: white;
            padding: 1.5rem 2rem;
            border: none;
            border-bottom: none;
            margin-bottom: 0;
            position: relative;
          }
          
          .dh-modal-title {
            color: white;
            font-weight: 700;
            font-size: 1.4rem;
            margin: 0;
          }
          
          /* Close button personalizado */
          .dh-close-btn {
            background: rgba(255, 255, 255, 0.2);
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 12px;
            color: white;
            padding: 8px 12px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          
          .dh-close-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            border-color: rgba(255, 255, 255, 0.5);
            color: white;
            transform: scale(1.05);
          }
          
          /* Body del modal */
          .dh-modal-body {
            padding: 2rem;
            background: linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%);
            border-top: none;
            margin-top: 0;
          }
          
          /* Tarjetas mejoradas */
          .dh-card {
            border: none;
            border-radius: 15px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.15);
            transition: all 0.3s ease;
          }
          
          .dh-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 15px 35px rgba(0,0,0,0.15);
          }
        `}
      </style>
      
      <div className="modal fade show d-block dh-modal-backdrop" tabIndex="-1">
      <div className="modal-dialog" style={{ maxWidth: '90vw', width: '1200px' }}>
        <div className="modal-content dh-modal-content" style={{ position: 'relative' }}>
          {/* Loading Overlay eliminado - ya no hay carga asíncrona */}
          
          {/* Header Profesional - Layout Original Mejorado */}
          <div className="modal-header" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '0',
            padding: '1.8rem 2rem 1.8rem 2rem',
            border: 'none',
            position: 'relative',
            boxShadow: 'none',
            marginBottom: '0',
            borderBottom: 'none'
          }}>
            <div className="d-flex align-items-center justify-content-between w-100">
              {/* Fecha del turno a la izquierda */}
              <div className="text-center" style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                padding: '16px 20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                minWidth: '160px'
              }}>
                <div style={{ 
                  fontSize: '0.65rem', 
                  fontWeight: '600',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  opacity: '0.9'
                }}>
                  <i className="fas fa-calendar-alt me-2"></i>
                  Fecha Turno
                </div>
                <div style={{ 
                  fontSize: '1.0rem', 
                  fontWeight: '700',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                }}>
                  {turnoSeleccionadoProp?.updated_at
                    ? new Date(turnoSeleccionadoProp.updated_at).toLocaleDateString("es-PE", { 
                        day: "2-digit",
                        month: "short", 
                        year: "numeric"
                      })
                    : "N/A"}
                </div>
              </div>

              {/* Título en el centro */}
              <div className="text-center">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    padding: '12px',
                    marginRight: '12px'
                  }}>
                    <i className="fas fa-lock" style={{ fontSize: '1.5rem' }}></i>
                  </div>
                  <h4 className="modal-title mb-0" style={{ 
                    fontWeight: '700', 
                    fontSize: '1.5rem',
                    letterSpacing: '0.5px'
                  }}>
                    Control Acceso Comensales
                  </h4>
                </div>
                <p className="mb-0" style={{ 
                  fontSize: '0.75rem', 
                  opacity: '0.9',
                  fontWeight: '300',
                  letterSpacing: '0.5px'
                }}>
                  Sistema de ingreso y registro
                </p>
              </div>

              {/* Contador de comensales a la derecha */}
              <div className="text-center" style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px',
                padding: '16px 20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                minWidth: '160px'
              }}>
                <div style={{ 
                  fontSize: '0.65rem', 
                  fontWeight: '600',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  opacity: '0.9'
                }}>
                  <i className="fas fa-users me-2"></i>
                  Registrados
                </div>
                <div style={{ 
                  fontSize: '1.9rem', 
                  fontWeight: '800',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                  lineHeight: '1'
                }}>
                  {cantidadComensales}
                </div>
              </div>
            </div>
          </div>

            {/* Botón cerrar posicionado absolutamente */}
            <button 
              type="button" 
              className="btn dh-close-btn" 
              onClick={onCloseProp}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid rgba(255, 255, 255, 1)',
                borderRadius: '12px',
                color: '#2c3e50',
                padding: '8px 12px',
                fontWeight: '700',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#ff416c';
                e.target.style.borderColor = '#ff416c';
                e.target.style.color = 'white';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 1)';
                e.target.style.color = '#2c3e50';
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Close"
            >
              ✕
            </button>
            
          </div>

          <div className="modal-body dh-modal-body" style={{ 
            paddingTop: '0.5rem',
            background: 'linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%)',
            marginTop: '0',
            borderTop: 'none'
          }}>
            {/* Información del turno con tarjetas compactas en una línea */}
            <div className="row mb-2">
              <div className="col-4">
                <div className="card h-100 dh-card" style={{
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white'
                }}>
                  <div className="card-body text-center p-2">
                    <i className="fas fa-store mb-1" style={{ fontSize: '1.1rem' }}></i>
                    <h6 className="card-title mb-1" style={{ fontWeight: '600', fontSize: '0.7rem' }}>Comedor</h6>
                    <p className="card-text mb-0" style={{ fontSize: '0.65rem', fontWeight: '500' }}>
                      {turnoSeleccionadoProp?.comedor?.nombre?.toUpperCase() || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="card h-100 dh-card" style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white'
                }}>
                  <div className="card-body text-center p-2">
                    <i className="fas fa-concierge-bell mb-1" style={{ fontSize: '1.1rem' }}></i>
                    <h6 className="card-title mb-1" style={{ fontWeight: '600', fontSize: '0.7rem' }}>Tipo Servicio</h6>
                    <p className="card-text mb-0" style={{ fontSize: '0.65rem', fontWeight: '500' }}>
                      {turnoSeleccionadoProp?.tipo_servicio_alimentacion?.descripcion || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="card h-100 dh-card" style={{
                  background: 'linear-gradient(135deg, #ff8c00 0%, #ff6b00 100%)',
                  color: 'white'
                }}>
                  <div className="card-body text-center p-2">
                    <i className="fas fa-barcode mb-1" style={{ fontSize: '1.1rem' }}></i>
                    <h6 className="card-title mb-1" style={{ fontWeight: '600', fontSize: '0.7rem' }}>Lectora</h6>
                    <p className="card-text mb-0" style={{ fontSize: '0.65rem', fontWeight: '500' }}>
                      {turnoSeleccionadoProp?.lectora?.nombre || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* Indicador de carga de datos en memoria */}
            {isLoadingMemoryData && (
              <div className="row mb-2 justify-content-center">
                <div className="col-md-10">
                  <div className="card" style={{
                    border: 'none',
                    borderRadius: '15px',
                    background: 'linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)',
                    color: 'white',
                    boxShadow: '0 8px 25px rgba(255, 193, 7, 0.3)'
                  }}>
                    <div className="card-body text-center py-3">
                      <div className="d-flex align-items-center justify-content-center">
                        <div className="spinner-border spinner-border-sm me-3" role="status" style={{
                          width: '1.5rem',
                          height: '1.5rem',
                          borderWidth: '3px'
                        }}>
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div>
                          <strong style={{ fontSize: '1.0rem' }}>
                            {isModalReady ? 'Datos cargados - Preparando...' : 'Cargando datos en memoria...'}
                          </strong>
                          <div style={{ fontSize: '0.8rem', opacity: '0.9' }}>
                            Documentos: {memoryDataLoaded.loadStateDocumentosComedor ? '✅' : '⏳'} | 
                            Dietas: {memoryDataLoaded.loadStateDietasComensal ? '✅' : '⏳'}
                            {isModalReady && ' | 🎉 ¡Listo!'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Campo de entrada principal con diseño futurista */}
            <div className="row mb-2 justify-content-center">
              <div className="col-md-10">
                <div className="card" style={{
                  border: 'none',
                  borderRadius: '20px',
                  background: 'linear-gradient(145deg, #2d3436 0%, #636e72 100%)',
                  padding: '1.5rem',
                  boxShadow: '0 15px 35px rgba(45, 52, 54, 0.4)'
                }}>
                  <input
                    type="text"
                    className="form-control text-center"
                    style={{
                      fontSize: '1.9rem',
                      height: '3.3rem',
                      fontWeight: 'bold',
                      letterSpacing: '2px',
                      background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                      border: '3px solid #17a2b8',
                      borderRadius: '15px',
                      color: '#2c3e50',
                      boxShadow: '0 0 30px rgba(23, 162, 184, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      position: 'relative',
                      zIndex: 10,
                      pointerEvents: 'auto',
                      cursor: 'text'
                    }}
                    placeholder={
                      isLoadingMemoryData ? "Cargando datos..." : 
                      isProcessing ? "Procesando..." : 
                      "📱 Escanee o ingrese documento"
                    }
                    value={numeroDocumentoComedor}
                    onChange={(e) => {
                      // Permitir solo números y letras, convertir a mayúsculas
                      const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      setNumeroDocumentoComedor(value);
                    }}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    disabled={isProcessing || isLoadingMemoryData}
                    ref={numeroDocumentoComedorInputRef}
                  />
                </div>
              </div>
            </div>

            {/* Mensajes de advertencia de antecedentes (historial de consumo duplicado) */}
            {resultadoRegistroComensal && resultadoRegistroComensal.tiene_historial_duplicado && (
              <div className="row mb-2">
                <div className="col-12">
                  <div 
                    className="alert alert-warning text-center"
                    style={{
                      border: 'none',
                      borderRadius: '15px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      boxShadow: '0 10px 25px rgba(255, 193, 7, 0.25)',
                      background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                      color: 'blue',
                      marginBottom: '10px'
                    }}
                  >
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {resultadoRegistroComensal.advertencia || "Advertencia: Este comensal registra incidencias previas por consumo duplicado de servicios."}
                  </div>
                </div>
              </div>
            )}

            {/* Mensajes de estado con animaciones */}
            <div className="row mb-2">
              <div className="col-12">
                {resultadoRegistroComensal && (
                  <div 
                    className={`alert ${resultadoRegistroComensal.color === 'green' ? 'alert-success' : 'alert-danger'} text-center`}
                    style={{
                      border: 'none',
                      borderRadius: '15px',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      boxShadow: resultadoRegistroComensal.color === 'green' 
                        ? '0 10px 25px rgba(40, 167, 69, 0.3)' 
                        : '0 10px 25px rgba(220, 53, 69, 0.3)',
                      background: resultadoRegistroComensal.color === 'green'
                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                        : 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
                      color: 'white'
                    }}
                  >
                    <i className={`fas ${resultadoRegistroComensal.color === 'green' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                    {typeof (resultadoRegistroComensal.message || resultadoRegistroComensal.error) === 'string' 
                      ? (resultadoRegistroComensal.message || resultadoRegistroComensal.error)
                      : JSON.stringify(resultadoRegistroComensal.message || resultadoRegistroComensal.error || 'Error desconocido')}
                  </div>
                )}
              </div>
            </div>

            {/* Información del comensal con efecto destacado */}
            <div className="row mb-2">
              <div className="col-12 text-center">
                {resultadoRegistroComensal && resultadoRegistroComensal.nombre && (
                  <div className="card" style={{
                    border: 'none',
                    borderRadius: '20px',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    padding: '1.5rem', // Reducido de 2rem a 1.5rem
                    boxShadow: '0 15px 35px rgba(0, 123, 255, 0.1)',
                    minHeight: '120px', // Reducido de 180px a 120px
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontSize: '2.1rem',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      textShadow: '0 0 30px rgba(0, 123, 255, 0.3)',
                      marginBottom: '0.75rem',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.1'
                    }} title={resultadoRegistroComensal.tipo_origen_trabajador?.nombre 
                      ? `${resultadoRegistroComensal.nombre} - ${resultadoRegistroComensal.tipo_origen_trabajador.nombre}`
                      : resultadoRegistroComensal.nombre}>
                      {(() => {
                        const nombreCompleto = resultadoRegistroComensal.tipo_origen_trabajador?.nombre
                          ? `${resultadoRegistroComensal.nombre} - ${resultadoRegistroComensal.tipo_origen_trabajador.nombre}`
                          : resultadoRegistroComensal.nombre;
                        return nombreCompleto.length > 45 ? `${nombreCompleto.substring(0, 45)}...` : nombreCompleto;
                      })()}
                    </div>
                    
                    {/* Mostrar información de dieta si existe - CON EFECTO PARPADEO */}
                    {dietaActivada && (
                      <>
                        <style>
                          {`
                            @keyframes dietaGlow {
                              0% { box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3); }
                              50% { box-shadow: 0 4px 25px rgba(255, 107, 53, 0.6), 0 0 20px rgba(255, 107, 53, 0.4); }
                              100% { box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3); }
                            }
                            .dieta-destacada {
                              animation: dietaGlow 2s infinite ease-in-out;
                            }
                            
                            /* Respetar configuraciones de accesibilidad */
                            @media (prefers-reduced-motion: reduce) {
                              .dieta-destacada {
                                animation: none;
                                box-shadow: 0 4px 20px rgba(255, 107, 53, 0.5) !important;
                                border: 4px solid #ff6b35 !important;
                              }
                            }
                          `}
                        </style>
                        <div className="dieta-destacada" style={{ 
                          fontSize: '1.4rem',
                          color: '#ff6b35',
                          fontWeight: '800',
                          textAlign: 'center',
                          marginTop: '15px',
                          marginBottom: '15px',
                          padding: '12px 20px',
                          backgroundColor: '#fff3cd',
                          border: '3px solid #ff6b35',
                          borderRadius: '15px',
                          textShadow: '0 0 15px rgba(255, 107, 53, 0.7)',
                          boxShadow: '0 4px 15px rgba(255, 107, 53, 0.3)'
                        }}>
                          <i className="fas fa-utensils me-2"></i>
                          🍽️ DIETA: {dietaActivada.tipo_dieta.nombre} 🍽️
                        </div>
                      </>
                    )}
                    
                    {resultadoRegistroComensal && resultadoRegistroComensal.id_cliente && (
                      <p className="mb-0" style={{ 
                        fontSize: '1.0rem',
                        color: '#6c757d',
                        fontWeight: '500',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={resultadoRegistroComensal.cliente?.nombre || 'Cliente no disponible'}>
                        <i className="fas fa-building me-2"></i>
                        {resultadoRegistroComensal.cliente?.nombre || 'Cliente no disponible'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Tabla de registros recientes modernizada */}
            <div className="row mt-2">
              <div className="col-12">
                <div className="card" style={{
                  border: 'none',
                  borderRadius: '20px',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                  minHeight: '400px' // Altura mínima fija para la tabla
                }}>
                  <div className="card-header" style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '20px 20px 0 0',
                    padding: '1.5rem 2rem'
                  }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="me-3" style={{
                          background: 'rgba(255,255,255,0.2)',
                          borderRadius: '10px',
                          padding: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <i className="fas fa-history" style={{ fontSize: '1.1rem' }}></i>
                        </div>
                        <div>
                          <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1.0rem' }}>
                            Registros Recientes
                          </h5>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="badge" style={{
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          padding: '8px 12px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          border: '2px solid rgba(255,255,255,0.3)'
                        }}>
                          <i className="fas fa-list-ol me-2"></i>
                          {recentRecords.length} / 5
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {recentRecords.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                            <tr>
                              <th scope="col" className="text-center" style={{ 
                                fontWeight: '600', 
                                color: '#495057',
                                padding: '0.9rem 0.65rem',
                                fontSize: '0.85rem'
                              }}>#</th>
                              <th scope="col" style={{ 
                                fontWeight: '600', 
                                color: '#495057',
                                padding: '0.9rem 0.65rem',
                                fontSize: '0.85rem'
                              }}>
                                <i className="fas fa-user me-2"></i>Nombre
                              </th>
                              <th scope="col" className="text-center" style={{ 
                                fontWeight: '600', 
                                color: '#495057',
                                padding: '0.9rem 0.65rem',
                                fontSize: '0.85rem'
                              }}>
                                <i className="fas fa-id-card me-2"></i>Documento
                              </th>
                              <th scope="col" style={{ 
                                fontWeight: '600', 
                                color: '#495057',
                                padding: '0.9rem 0.65rem',
                                fontSize: '0.85rem'
                              }}>
                                <i className="fas fa-building me-2"></i>Cliente
                              </th>
                              <th scope="col" className="text-center" style={{ 
                                fontWeight: '600', 
                                color: '#495057',
                                padding: '0.9rem 0.65rem',
                                fontSize: '0.85rem'
                              }}>
                                <i className="fas fa-clock me-2"></i>Hora
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentRecords.map((record, index) => (
                              <tr 
                                key={record.id} 
                                className={record.isNewRecord ? 'table-success' : ''} 
                                style={record.isNewRecord ? {
                                  background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                                  borderLeft: '4px solid #28a745',
                                  animation: 'fadeIn 0.5s ease-in'
                                } : index === 0 && !record.isNewRecord ? {
                                  background: 'linear-gradient(135deg, #e2e3e5 0%, #d6d8db 100%)',
                                  borderLeft: '4px solid #6c757d'
                                } : {}}
                              >
                                <td className="text-center" style={{ padding: '0.9rem 0.65rem' }}>
                                  <span className="badge" style={{
                                    background: record.isNewRecord 
                                      ? '#28a745' 
                                      : index === 0 && !record.isNewRecord 
                                        ? '#6c757d' 
                                        : '#6c757d',
                                    fontSize: '0.7rem',
                                    padding: '0.45rem',
                                    position: 'relative'
                                  }}>
                                    {record.isNewRecord && (
                                      <i className="fas fa-star" style={{ 
                                        fontSize: '0.6rem', 
                                        marginRight: '0.2rem' 
                                      }}></i>
                                    )}
                                    {index + 1}
                                  </span>
                                </td>
                                <td style={{ 
                                  fontWeight: '600',
                                  color: '#495057',
                                  padding: '0.9rem 0.65rem',
                                  fontSize: '0.85rem',
                                  maxWidth: '280px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }} title={record.nombre}>
                                  {record.nombre}
                                </td>
                                <td className="text-center" style={{ padding: '0.9rem 0.65rem' }}>
                                  <code style={{
                                    background: '#f8f9fa',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {record.numero_documento_comedor}
                                  </code>
                                </td>
                                <td style={{ 
                                  color: '#6c757d',
                                  padding: '0.9rem 0.65rem',
                                  fontSize: '0.85rem',
                                  maxWidth: '250px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }} title={record.cliente || 'N/A'}>
                                  {record.cliente || 'N/A'}
                                </td>
                                <td className="text-center" style={{ padding: '0.9rem 0.65rem' }}>
                                  <span className="badge bg-primary" style={{
                                    fontSize: '0.75rem',
                                    padding: '0.45rem 0.65rem',
                                    borderRadius: '10px'
                                  }}>
                                    {record.timestamp}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5" style={{ color: '#6c757d' }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          borderRadius: '20px',
                          padding: '3rem 2rem',
                          margin: '2rem'
                        }}>
                          <i className="fas fa-clipboard-list" style={{ 
                            fontSize: '3rem', 
                            marginBottom: '1rem',
                            color: '#adb5bd'
                          }}></i>
                          <h5 style={{ fontWeight: '300', marginBottom: '0.5rem' }}>
                            Sin registros aún
                          </h5>
                          <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                            Los registros aparecerán aquí en tiempo real
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Modal de Selección de Comensales */}
      {showComensalSelector && (
        <div 
          className="modal fade show d-block" 
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 1060
          }}
          onClick={(e) => e.stopPropagation()} // Evitar cierre por click fuera
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{
              borderRadius: '20px',
              border: 'none',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                borderRadius: '20px 20px 0 0',
                border: 'none'
              }}>
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Selección de Empresa Requerida
                </h5>
              </div>
              <div className="modal-body" style={{ padding: '2rem' }}>
                <div className="alert alert-warning" style={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)',
                  border: 'none',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '10px'
                }}>
                  <h6 style={{ color: '#92400e', fontWeight: '600' }}>
                    <i className="fas fa-info-circle me-2"></i>
                    El documento <strong>{documentoAmbiguo}</strong> existe en múltiples empresas activas
                  </h6>
                  <p className="mb-0" style={{ color: '#92400e' }}>
                    <strong>Debe seleccionar una empresa para continuar.</strong> Para evitar esta situación en el futuro, desactive los comensales duplicados en el mantenimiento.
                  </p>
                </div>

                <h6 className="mb-3" style={{ 
                  color: '#374151', 
                  fontWeight: '600',
                  textAlign: 'center',
                  fontSize: '1.1rem'
                }}>
                  <i className="fas fa-hand-pointer me-2" style={{ color: '#f59e0b' }}></i>
                  Seleccione la empresa correcta:
                </h6>

                <div className="row">
                  {comensalesMultiples.map((comensal, index) => (
                    <div key={comensal.id} className="col-md-6 mb-3">
                      <div 
                        className="card h-100" 
                        style={{
                          border: '2px solid #e5e7eb',
                          borderRadius: '15px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => handleComensalSelection(comensal)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#3b82f6';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div className="card-body text-center" style={{ padding: '1.5rem' }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            background: `linear-gradient(135deg, ${index === 0 ? '#3b82f6' : '#10b981'} 0%, ${index === 0 ? '#1d4ed8' : '#059669'} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 1rem auto',
                            color: 'white',
                            fontSize: '1.5rem',
                            fontWeight: '600'
                          }}>
                            {comensal.nombre.charAt(0).toUpperCase()}
                          </div>
                          <h6 style={{ 
                            color: '#374151', 
                            fontWeight: '600',
                            marginBottom: '0.5rem'
                          }}>
                            {comensal.tipo_origen_trabajador?.nombre
                              ? `${comensal.nombre} - ${comensal.tipo_origen_trabajador.nombre}`
                              : comensal.nombre}
                          </h6>
                          <p style={{ 
                            color: '#6b7280', 
                            fontSize: '0.9rem',
                            marginBottom: '0.5rem',
                            fontWeight: '500'
                          }}>
                            {comensal.cliente}
                          </p>
                          {comensal.dieta_activa ? (
                            <div style={{
                              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                              border: '1px solid #16a34a',
                              borderRadius: '8px',
                              padding: '0.5rem',
                              marginBottom: '0.5rem'
                            }}>
                              <small style={{ 
                                color: '#15803d', 
                                fontWeight: '600',
                                fontSize: '0.75rem'
                              }}>
                                <i className="fas fa-utensils me-1"></i>
                                {comensal.dieta_activa.tipo_dieta}
                              </small>
                              {comensal.dieta_activa.descripcion && (
                                <div style={{ 
                                  color: '#166534', 
                                  fontSize: '0.7rem',
                                  marginTop: '0.2rem'
                                }}>
                                  {comensal.dieta_activa.descripcion}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{
                              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              padding: '0.5rem',
                              marginBottom: '0.5rem'
                            }}>
                              <small style={{ 
                                color: '#64748b', 
                                fontWeight: '500',
                                fontSize: '0.75rem'
                              }}>
                                <i className="fas fa-utensils me-1"></i>
                                Sin dieta especial
                              </small>
                            </div>
                          )}
                          <div className="mt-2">
                            <span className="badge" style={{
                              background: `linear-gradient(135deg, ${index === 0 ? '#3b82f6' : '#10b981'} 0%, ${index === 0 ? '#1d4ed8' : '#059669'} 100%)`,
                              color: 'white',
                              padding: '0.5rem 1rem',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}>
                              Seleccionar
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalTurnoDetalle;
